/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {dlsColor} from '@ion/dls/src/theme/theme';
import CloseButton from '../components/buttons/CloseButton';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import {clearNotificationDialogBaseStyles} from './ClearNotificationDialogStyles';
import NotificationsClearDialog from './NotificationsClearDialog';

export interface ClearNotificationsDialogProps {
  onDismiss?: () => void;
  onAction?: () => void;
};

const sharedTextStyles = {
  fontFamily    : 'Inter',
  fontSize      : '14px',
  lineHeight    : '20px',
  letterSpacing : '0'
} as const;

const useStyles = makeStyles(() => ({
  ...clearNotificationDialogBaseStyles(),
  clearText : {
    ...sharedTextStyles,
    color      : dlsColor('grey1'),
    fontWeight : 400
  },
  urgentText : {
    ...sharedTextStyles,
    color      : dlsColor('grey1'),
    fontWeight : 700
  }
}));

/**
 * Component for rendering clear notifications dialog.
 *
 * ```typescript
 * 
 * import {ClearNotificationsDialog} from '@ion/dls/src/notifications'
 * 
 * ```
 *
 */
export const ClearNotificationsDialog = ({
  onDismiss,
  onAction
}: ClearNotificationsDialogProps) => {
  const {isPhone} = useMedia();
  const classes   = useStyles({isPhone});
  const {message} = translate('notifications:panel.urgent', {returnObjects: true}) || {};

  const getMessageBody = () => (
    <>
      <p className={classes.clearText}>
        {message?.prefix}{' '}
        <span className={classes.urgentText}>
          {message?.bold}
        </span>{' '}
        {message?.suffix}
      </p>
    </>
  );

  return (
    <Dialog open classes={{
      root        : classes.userNotificationRoot,
      scrollPaper : classes.scrollPaper,
      paper       : classes.paper
    }}>
      <DialogTitle className={classes.title}>
        <span className={classes.titleText}>
          {translate('notifications:panel.urgent.title')}
        </span>
        <div className={classes.currentCloseButton}>
          <CloseButton onClick={onDismiss} />
        </div>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <DialogContentText className={classes.contentText}>
          {getMessageBody()}
        </DialogContentText>
        <NotificationsClearDialog />
      </DialogContent>
      <DialogActions className={classes.actions}>
        <TertiaryButton onClick={onAction}>
          {translate('notifications:panel.urgent.actionLabel')}
        </TertiaryButton>
        <TertiaryButton onClick={onDismiss}>
          {translate('notifications:panel.urgent.dismissLabel')}
        </TertiaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(ClearNotificationsDialog);
